/**
 * ScenarioTemplatePickerModal (P12.3).
 *
 * Admin-only modal listing the seeded scenario templates grouped by
 * area. Click "Instantiate" on a row -> POST and navigate the rail
 * to the new scenario id on success.
 */
import { useTranslation } from "react-i18next";
import { useToast } from "shared/toast/ToastProvider";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { ScenarioArea, ScenarioTemplate } from "shared/api/types";
import { useInstantiateTemplate, useScenarioTemplates } from "twin/api/scenario_hooks";

const AREA_LABEL: Record<ScenarioArea, string> = {
  cyber_technology: "Cyber / Technology",
  operational_third_party: "Operational / Third-Party",
  physical_environmental: "Physical / Environmental",
};

const AREA_TONE: Record<ScenarioArea, string> = {
  cyber_technology: "border-violet-300 bg-violet-50",
  operational_third_party: "border-amber-300 bg-amber-50",
  physical_environmental: "border-teal-300 bg-teal-50",
};

interface Props {
  onClose: () => void;
}

export function ScenarioTemplatePickerModal({ onClose }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const templates = useScenarioTemplates();
  const instantiate = useInstantiateTemplate();
  const toast = useToast();

  // Round 14 D9 -- the name the new scenario will carry.
  const [name, setName] = useState("");
  // Round 14 D9 -- SAMA's nine are SAMA's nine: one group, in SAMA's
  // order, named for what they are. Grouped by the old cause area they
  // were scattered among other templates and nothing said which nine
  // the regulator had sent.
  const { sama, grouped } = useMemo(() => {
    const groups: Record<ScenarioArea, ScenarioTemplate[]> = {
      cyber_technology: [],
      operational_third_party: [],
      physical_environmental: [],
    };
    const mandated: ScenarioTemplate[] = [];
    for (const tpl of Array.isArray(templates.data) ? templates.data : []) {
      if (tpl.code.startsWith("tpl.sama.")) mandated.push(tpl);
      else groups[tpl.area]?.push(tpl);
    }
    mandated.sort((a, b) => a.display_order - b.display_order);
    return { sama: mandated, grouped: groups };
  }, [templates.data]);

  const handlePick = async (code: string) => {
    try {
      const created = await instantiate.mutateAsync({
        templateCode: code,
        ...(name.trim() ? { name: name.trim() } : {}),
      });
      onClose();
      // D9 -- it navigated silently. The reader landed on a scenario
      // page with no statement that one had just been created, so
      // "did that work?" was answered by inference from the URL.
      toast.push({
        tone: "success",
        message: t("screens.scenarioTemplatePickerModal.created", {
          name: created.name,
        }),
      });
      navigate(`/twin/scenarios/${encodeURIComponent(created.id)}`);
    } catch {
      // Error surfaces via instantiate.error inside the modal.
    }
  };

  return (
    <div
      onClick={onClose}
      data-testid="scenario-template-picker-modal"
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/30 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg border border-slate-300 bg-white p-4 shadow-xl space-y-3"
      >
        <header className="flex items-baseline justify-between">
          <h3 className="text-sm font-bold text-slate-900">
            {t("screens.scenarioTemplatePickerModal.pickATemplateToInstantiate")}
          </h3>
          <button
            type="button"
            data-testid="picker-close"
            onClick={onClose}
            className="text-[10px] uppercase tracking-wider text-slate-700 hover:underline"
          >
            {t("screens.scenarioTemplatePickerModal.close")}
          </button>
        </header>

        {templates.isLoading && (
          <p className="text-xs text-slate-500">
            {t("screens.scenarioTemplatePickerModal.loadingTemplates")}
          </p>
        )}

        {instantiate.error && (
          <p
            data-testid="picker-error"
            className="rounded border border-rose-300 bg-rose-50 p-2 text-xs text-rose-800"
          >
            {t("screens.scenarioTemplatePickerModal.instantiateFailed")}{" "}
            {instantiate.error instanceof Error
              ? instantiate.error.message
              : t("screens.scenarioTemplatePickerModal.unknown")}
          </p>
        )}

        <label className="block text-xs text-slate-700">
          {t("screens.scenarioTemplatePickerModal.nameLabel")}
          <input
            dir="auto"
            data-testid="picker-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("screens.scenarioTemplatePickerModal.namePlaceholder")}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </label>

        {sama.length > 0 && (
          <section data-testid="picker-group-sama" className="space-y-1">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
              {t("screens.scenarioTemplatePickerModal.samaHeading", {
                count: sama.length,
              })}
            </h4>
            <p className="text-[11px] text-slate-600">
              {t("screens.scenarioTemplatePickerModal.samaNote")}
            </p>
            {sama.map((tpl, i) => (
              <div
                key={tpl.code}
                data-testid="picker-template-row"
                data-template-code={tpl.code}
                className="flex items-start justify-between gap-3 rounded border border-brand-brass/40 bg-brand-brass/5 p-2 text-xs"
              >
                <div className="flex-1">
                  <div className="font-semibold text-slate-900">
                    {i + 1}. {tpl.name}
                  </div>
                  {tpl.description && (
                    <p className="mt-1 whitespace-pre-line text-slate-700">
                      {tpl.description}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  data-testid="picker-instantiate-button"
                  onClick={() => void handlePick(tpl.code)}
                  disabled={instantiate.isPending}
                  className="rounded border border-brand-brassDark bg-brand-brass px-3 py-1 text-[11px] font-semibold text-brand-navy disabled:opacity-50"
                >
                  {instantiate.isPending
                    ? "…"
                    : t("screens.scenarioTemplatePickerModal.instantiate")}
                </button>
              </div>
            ))}
          </section>
        )}

        {(
          [
            "cyber_technology",
            "operational_third_party",
            "physical_environmental",
          ] as ScenarioArea[]
        ).map((area) => {
          const list = grouped[area];
          if (list.length === 0) return null;
          return (
            <section
              key={area}
              data-testid={`picker-group-${area}`}
              className="space-y-1"
            >
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {AREA_LABEL[area]}
              </h4>
              {list.map((tpl) => (
                <div
                  key={tpl.code}
                  data-testid="picker-template-row"
                  data-template-code={tpl.code}
                  className={`flex items-start justify-between gap-3 rounded border p-2 text-xs ${AREA_TONE[area]}`}
                >
                  <div className="flex-1">
                    <div className="font-semibold text-slate-900">{tpl.name}</div>
                    <div className="font-mono text-[10px] text-slate-500">
                      {tpl.code}
                    </div>
                    {tpl.description && (
                      <p className="mt-1 whitespace-pre-line text-slate-700">
                        {tpl.description}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    data-testid="picker-instantiate-button"
                    onClick={() => void handlePick(tpl.code)}
                    disabled={instantiate.isPending}
                    className="rounded border border-brand-brassDark bg-brand-brass px-3 py-1 text-[11px] font-semibold text-brand-navy disabled:opacity-50"
                  >
                    {instantiate.isPending
                      ? "…"
                      : t("screens.scenarioTemplatePickerModal.instantiate")}
                  </button>
                </div>
              ))}
            </section>
          );
        })}
      </div>
    </div>
  );
}
